import { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { X, Info } from "lucide-react";
import { useAuth } from "../../../../../common/store/authStore";
import { fetchChatMessages, sendChatMessage } from "../../../../../common/services/chat";
import "../styles/live-chat.css";
import { BACKEND_URL } from "../../../../../common/config/config";

interface Message {
  userId: string;
  username: string;
  message: string;
  timestamp: Date;
}

interface LiveChatProps {
  tournamentId: string;
  isHost: boolean;
  participants: string[];
  status: string;
  onClose: () => void;
}

const LiveChat = ({ tournamentId, isHost, participants, status, onClose }: LiveChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [socket, setSocket] = useState<Socket | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { authenticatedUser } = useAuth();

  const canSendMessage = true; // Open chat for everyone
  const isTournamentUpcoming = status === "upcoming";
  const isTournamentCompleted = status === "completed";

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const msgs = await fetchChatMessages(tournamentId);
        setMessages(msgs);
      } catch {
        setMessages([]);
      }
    };

    loadMessages();

    const newSocket = io(BACKEND_URL);
    setSocket(newSocket);

    newSocket.emit("join-tournament", tournamentId);

    newSocket.on("new-message", (message: Message) => {
      setMessages((prev) => [...prev, message]);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [tournamentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !authenticatedUser) return;

    try {
      const message = await sendChatMessage(tournamentId, newMessage);

      socket?.emit("send-message", {
        tournamentId,
        message,
      });

      setNewMessage("");
    } catch {
      return;
    }
  };

  return (
    <div className="live-chat-panel">
      <div className="chat-header">
        <h3>Live Chat</h3>
        <X className="close-icon" onClick={onClose}/>
      </div>

      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className="chat-message">
            <span className="message-username">{msg.username}</span>
            <span className="message-text">{msg.message}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>  

      {canSendMessage && status === "live" && (
        <div className="chat-input-container">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            placeholder="Type a message..."
            className="chat-input"
          />
          <button onClick={handleSendMessage} className="send-btn">
            Send
          </button>
        </div>
      )}

      {isTournamentUpcoming ? (
        <div className="chat-info-container">
          <Info 
            className="chat-info-icon" 
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          />
          <span className="chat-info-text">Tournament hasn't started</span>
          {showTooltip && (
            <div className="chat-tooltip">
              The tournament hasn't started yet. Chat will be available once the tournament goes live.
            </div>
          )}
        </div>
      ) : isTournamentCompleted ? (
        <div className="chat-info-container">
          <Info 
            className="chat-info-icon" 
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          />
          <span className="chat-info-text">Tournament ended</span>
          {showTooltip && (
            <div className="chat-tooltip">
              This tournament has ended. Chat is no longer available.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default LiveChat;
